import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { H2 } from "../../../../components/basics";
import { Accordion, AccordionSection } from "../../../../components/interactives";
import { termsConditionsValidations } from "../../../../helpers/professional_indemnity/validations";
import useForm from "../../../../hooks/useForm";

import classNamePresets from "../../../../utils/classNamePresets";
import ServerError from "../../../errors/ServerError";
import TermsConditions from "../../components/Terms";
import { IndemnityFormContext } from "../../NewProduct";
import IndemnityFormStepNavigation from "../IndemnityFormStepNavigation";
import DeclarationTerms from "./terms/DeclarationTerms";
import ManulifeSvImportantNoticesTerms from "./terms/ManulifeSV/ImportantNoticeTerms";
import ManulifeSvDeclarationTerms from "./terms/ManulifeSV/DeclarationTerms";
import ImportantNoticesTerms from "./terms/ImportantNoticesTerms";

type Props = {
  onNext: VoidFunction | null;
  onPrevious: VoidFunction | null;
  isNewTranslationFlow?: boolean;
};

const Terms: FC<Props> = ({ onNext, onPrevious, isNewTranslationFlow }) => {
  const { t } = useTranslation("pages", { keyPrefix: "professionalIndemnity.newProduct" });
  const { state, onChange, isSubmitting, submitError } = useContext(IndemnityFormContext);
  const form = useForm(state, termsConditionsValidations, onChange);
  const [serverError, setServerError] = useState(false);
  const policyWordingUrl = isNewTranslationFlow
    ? "/documents/Manulife SV Group PI Policy (Allied World).pdf"
    : "/documents/ManulifeProfessionalIndemnityPolicyWordings.pdf";
  const isSvFlow = Boolean(isNewTranslationFlow);
  const ImportantNoticesComponent = isSvFlow ? ManulifeSvImportantNoticesTerms : ImportantNoticesTerms;
  const DeclarationComponent = isSvFlow ? ManulifeSvDeclarationTerms : DeclarationTerms;
  const {
    field: { accepted, promotionalServices },
  } = form;
  const handleNext = () => {
    onNext && form.validateForm(onNext);
  };

  useEffect(() => {
    if (submitError) {
      setServerError(true);
    }
  }, [submitError]);

  return (
    <>
      <div className="container-flow flex-1">
        <H2 className={classNamePresets.mainHeading}>{t("steps.terms")}</H2>
        <p className="mt-8">{t("terms.reviewInformation")}</p>
        <Accordion id="terms-info" omitMarginTop>
          <AccordionSection id="important-notices" label={t("terms.importantNotices")}>
            <ImportantNoticesComponent />
          </AccordionSection>
          <AccordionSection id="declaration" label={t("terms.declaration")}>
            <DeclarationComponent />
          </AccordionSection>
          <TermsConditions
            isAccordionExpanded={true}
            state={state}
            t={t}
            promotionalServices={promotionalServices}
            accepted={accepted}
            policyWordingUrl={policyWordingUrl}
            isManulifesv={isNewTranslationFlow}
          />
        </Accordion>
      </div>
      {serverError && <ServerError supportEmailId={t("customerSupport.emailId")} displayType="modal" onClose={() => setServerError(false)} />}
      <IndemnityFormStepNavigation isLoading={isSubmitting} onBack={onPrevious} onSubmit={handleNext} />
    </>
  );
};

export default Terms;
